import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard, Wrench,
  Settings, LogOut, Bell, ChevronLeft, ChevronRight,
  Target, Boxes, Building2, TrendingUp, Truck, MessageSquare,
  Menu, X, Shield, FileText, Package, ShoppingCart, Sun, Moon
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { getAuthToken } from '../utils/authToken';
import { cachedFetch } from '../utils/dataCache';
import { useTheme } from '@/contexts/ThemeContext';
import GlobalSearch from '@/components/common/GlobalSearch';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/sourcing', label: 'Batch Center', icon: Target },
  { path: '/stock', label: 'Stock', icon: Boxes },
  { path: '/sales', label: 'Sales & Customers', icon: TrendingUp },
  { path: '/preorders', label: 'Pre-Orders', icon: ShoppingCart },
  { path: '/pricelist', label: 'Pricelist', icon: FileText },
  { path: '/logistics', label: 'Logistics', icon: Truck },
  { path: '/competitors', label: 'Market Intel', icon: Building2 },
  { path: '/repairs', label: 'Repairs', icon: Wrench },
  { path: '/parts', label: 'Parts', icon: Package },
  { path: '/icasa', label: 'ICASA Licensing', icon: Shield },
  { path: '/support', label: 'Support', icon: MessageSquare },
  { path: '/settings', label: 'Settings', icon: Settings },
];

const roleAccess = {
  superadmin: navItems.map(i => i.path),
  admin: navItems.map(i => i.path),
  finance: ['/dashboard', '/sourcing', '/stock', '/sales', '/preorders', '/pricelist', '/logistics', '/competitors', '/icasa', '/support', '/settings'],
  sales: ['/dashboard', '/stock', '/sales', '/preorders', '/pricelist', '/logistics', '/competitors', '/support', '/settings'],
  technician: ['/dashboard', '/stock', '/repairs', '/parts', '/support', '/settings'],
  viewer: ['/dashboard', '/stock', '/support'],
  partner: ['/dashboard', '/sourcing', '/pricelist', '/logistics', '/icasa', '/support'],
};

export default function Layout({ children }: any) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [showAlerts, setShowAlerts] = useState(false);

  const accessPaths = roleAccess[user?.role] || [];
  const filteredNav = navItems.filter(item => accessPaths.includes(item.path));

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        // Use cached fetch for alerts - 60 second cache
        const res = await cachedFetch(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/alerts`, {
          headers: { Authorization: `Bearer ${getAuthToken()}` }
        }, 60000);
        if (res.ok) setAlerts(await res.json());
      } catch (e) { /* silent */ }
    };
    fetchAlerts();
    // Refresh alerts every 2 minutes instead of 1 (less network calls)
    const interval = setInterval(fetchAlerts, 120000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => { logout(); navigate('/login'); };

  const roleBadgeColor = {
    superadmin: 'bg-purple-600 text-white',
    admin: 'bg-teal-600 text-white',
    finance: 'bg-emerald-600 text-white',
    sales: 'bg-blue-600 text-white',
    technician: 'bg-amber-500 text-white',
    viewer: 'bg-gray-500 text-white',
    partner: 'bg-blue-600 text-white',
  };

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Desktop: always visible, Mobile: slide-out drawer */}
      <aside
        data-testid="sidebar"
        className={`
          fixed lg:relative inset-y-0 left-0 z-50
          flex flex-col transition-all duration-300 ease-in-out
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${collapsed ? 'lg:w-[64px]' : 'w-[260px] lg:w-[240px]'}
        `}
        style={{ background: '#0D1B2A' }}
      >
        {/* Logo */}
        <div className="flex items-center justify-between px-4 h-16 border-b border-white/10">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm" style={{ background: '#1B7F79' }}>CP</div>
            {(!collapsed || mobileMenuOpen) && (
              <span className="text-white font-bold text-lg tracking-tight">Cell Parts</span>
            )}
          </div>
          {/* Close button on mobile */}
          <button
            className="lg:hidden text-white/70 hover:text-white p-1"
            onClick={() => setMobileMenuOpen(false)}
          >
            <X size={24} />
          </button>
          {/* Collapse button on desktop */}
          <button
            data-testid="sidebar-toggle"
            onClick={() => setCollapsed(!collapsed)}
            className="hidden lg:block text-white/50 hover:text-white/90 transition-colors p-1"
          >
            {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
          {filteredNav.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/dashboard'}
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''} ${collapsed && !mobileMenuOpen ? 'justify-center px-2' : ''}`}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon size={20} />
              {(!collapsed || mobileMenuOpen) && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* User info */}
        {(!collapsed || mobileMenuOpen) && (
          <div className="p-3 border-t border-white/10">
            <div className="text-white/80 text-sm font-medium truncate">{user?.name}</div>
            <div className="text-white/40 text-xs truncate">{user?.email}</div>
            {/* Mobile logout button */}
            <button
              onClick={handleLogout}
              className="lg:hidden mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white/10 text-white/80 hover:bg-white/20 transition-colors text-sm"
            >
              <LogOut size={16} />
              <span>Logout</span>
            </button>
          </div>
        )}
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden w-full">
        {/* Top bar */}
        <header data-testid="top-bar" className="h-14 lg:h-16 bg-white border-b border-gray-200 flex items-center justify-between gap-3 px-3 lg:px-6 flex-shrink-0 app-topbar">
          <div className="flex items-center gap-3">
            {/* Mobile hamburger menu */}
            <button
              className="lg:hidden p-2 -ml-1 rounded-lg hover:bg-gray-100 transition-colors"
              onClick={() => setMobileMenuOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu size={24} className="text-gray-700" />
            </button>
            <h1 className="hidden sm:block text-base lg:text-lg font-semibold text-gray-800 app-topbar-title">Cell Parts</h1>
          </div>
          {/* Global search — takes the middle space so it's reachable from every page. */}
          <div className="flex-1 flex justify-center max-w-xl mx-auto">
            <GlobalSearch />
          </div>
          <div className="flex items-center gap-2 lg:gap-4">
            {/* Theme toggle */}
            <button
              data-testid="theme-toggle"
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-600 dark-topbar-btn"
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            {/* Notifications */}
            <div className="relative">
              <button
                data-testid="notifications-bell"
                onClick={() => setShowAlerts(!showAlerts)}
                className={`relative p-2 rounded-lg hover:bg-gray-100 transition-colors ${alerts.length > 0 ? 'text-teal-700' : ''}`}
              >
                <Bell size={20} className={`text-gray-600 ${alerts.length > 0 ? 'animate-bell-swing' : ''}`} />
                {alerts.length > 0 && (
                  <>
                    <span
                      key={`badge-${alerts.length}`}
                      data-testid="notifications-count-badge"
                      className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center text-white animate-badge-pop"
                      style={{ background: '#C0392B' }}
                    >
                      {alerts.length}
                    </span>
                    <span
                      aria-hidden
                      className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full animate-ping-slow"
                      style={{ background: '#C0392B', opacity: 0.45 }}
                    />
                  </>
                )}
              </button>
              {showAlerts && (
                <div className="absolute right-0 top-12 w-72 lg:w-80 bg-white rounded-lg shadow-xl border z-50 max-h-80 overflow-y-auto animate-fade-in">
                  <div className="p-3 border-b font-semibold text-sm">Alerts ({alerts.length})</div>
                  {alerts.length === 0 && <div className="p-4 text-sm text-gray-500">No active alerts</div>}
                  {alerts.map((a, i) => (
                    <div key={i} className={`p-3 border-b text-sm flex items-start gap-2 ${a.type === 'error' ? 'bg-red-50' : 'bg-amber-50'}`}>
                      <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${a.type === 'error' ? 'bg-red-500' : 'bg-amber-500'}`} />
                      <span>{a.message}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Role badge - hidden on small mobile */}
            <span data-testid="role-badge" className={`hidden sm:inline-flex px-2 lg:px-3 py-1 rounded-full text-xs font-semibold capitalize ${roleBadgeColor[user?.role] || 'bg-gray-200'}`}>
              {user?.role}
            </span>

            {/* Logout - hidden on mobile (shown in sidebar) */}
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="hidden lg:block p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-600"
            >
              <LogOut size={20} />
            </button>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-3 lg:p-6 bg-[#F2F4F6] app-main-content">
          {children}
        </main>
      </div>

      {/* Click outside alerts to close */}
      {showAlerts && <div className="fixed inset-0 z-40" onClick={() => setShowAlerts(false)} />}
    </div>
  );
}
